import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import ScoreTrendChart from '../../components/ScoreTrendChart';
import TrustStack from '../../components/TrustStack';
import RuleTriggerList from '../../components/RuleTriggerList';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import SuggestContextChangeModal from '../../components/SuggestContextChangeModal';
import PatrolPlanCard from '../../components/PatrolPlanCard';
import { vendorApi } from '../../services/vendor';
import type { PatrolServicePlan } from '../../types';
import { scoringApi } from '../../services/scoring';
import extractApiError from '../../utils/extractApiError';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

const statusVariant = (s: string) =>
  s === 'active' ? 'success' : s === 'pending' ? 'warning' : 'error';

/**
 * Read-only view of the patrol plan(s) the client has defined for this
 * site, plus an Acknowledge button that stamps vendor_acknowledged_at
 * on the plan.  Sprint 2's matcher writes this state onto every visit
 * verdict so the dispute UI can flag "graded against a plan you never
 * saw" cases.
 */
function VendorPatrolPlanSection({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['vendor', 'patrol-plans', siteId],
    queryFn: async () => {
      // Pull only THIS site's plans — filtering server-side dodges the
      // paginate-past-page-1 bug when a vendor is assigned to many sites.
      const r = await vendorApi.getPatrolPlans({ coverage__site: siteId });
      return r.data.results ?? [];
    },
  });

  const ack = useMutation({
    mutationFn: (planId: string) => vendorApi.acknowledgePatrolPlan(planId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor', 'patrol-plans', siteId] });
      setError(null);
    },
    onError: (err: unknown) => setError(extractApiError(err)),
  });

  const plans: PatrolServicePlan[] = data ?? [];

  if (!plans.length) return null;

  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-1">Patrol Plan</h3>
      <p className="text-xs text-slate-500 mb-3">
        The visits the client expects from you. Acknowledge to confirm
        you've seen the plan.
      </p>
      {error && (
        <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-700">
          {error}
        </div>
      )}
      {plans.map((plan) => (
        <div key={plan.id} className="mb-4 last:mb-0">
          <PatrolPlanCard plan={plan} />
          <div className="mt-3 flex items-center gap-3">
            {plan.vendor_acknowledged_at ? (
              <p className="text-xs text-emerald-700">
                Acknowledged on {new Date(plan.vendor_acknowledged_at).toLocaleDateString()}
                {plan.vendor_acknowledged_by_email ? ` by ${plan.vendor_acknowledged_by_email}` : ''}.
              </p>
            ) : (
              <p className="text-xs text-amber-700">Not yet acknowledged.</p>
            )}
            <button
              onClick={() => ack.mutate(plan.id)}
              disabled={ack.isPending}
              className="ml-auto px-3 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50"
            >
              {ack.isPending
                ? 'Saving...'
                : plan.vendor_acknowledged_at ? 'Re-acknowledge' : 'Acknowledge'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


export default function VendorSiteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'accept' | 'reject' | null>(null);
  const [suggestModalOpen, setSuggestModalOpen] = useState(false);

  const { data: site, isLoading, isError } = useQuery({
    queryKey: ['vendor', 'sites', id],
    queryFn: () => vendorApi.getSite(id!).then((r) => r.data),
    enabled: !!id,
  });

  const { data: scoreHistory } = useQuery({
    queryKey: ['vendor', 'site-score-history', id],
    queryFn: () => scoringApi.getVendorScoreHistory('site', id!),
    enabled: !!id,
  });

  const { data: ruleTriggers } = useQuery({
    queryKey: ['vendor', 'site-rule-triggers', id],
    queryFn: () => scoringApi.getVendorRuleTriggers('site', id!),
    enabled: !!id,
  });

  const acceptMutation = useMutation({
    mutationFn: (action: 'accept' | 'reject') => {
      setPendingAction(action);
      return vendorApi.acceptSite(id!, action);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', 'sites'] });
      setAcceptError(null);
      setPendingAction(null);
    },
    onError: (err: unknown) => {
      setAcceptError(extractApiError(err));
      setPendingAction(null);
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500">Failed to load site. Please try again later.</p>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-slate-500">Site not found</p>
      </div>
    );
  }

  const isPending = site.vendor_assignment_status === 'pending';
  const snapshots = scoreHistory?.results ?? [];
  const previousScore = snapshots.length >= 2
    ? Number(snapshots[snapshots.length - 2].score_value)
    : null;

  return (
    <>
      <PageHeader
        title={site.name}
        description={`${site.address}, ${site.city}, ${site.state} ${site.zip_code}`}
        actions={
          <button
            onClick={() => navigate('/vendor/sites')}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
          >
            Back to Sites
          </button>
        }
      />

      {/* Pending invitation banner */}
      {isPending && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-amber-800">Site Invitation Pending</p>
            <p className="text-xs text-amber-600 mt-1">
              {site.client_org_name} has invited your organization to service this site.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => acceptMutation.mutate('accept')}
              disabled={acceptMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
            >
              {pendingAction === 'accept' ? 'Accepting...' : 'Accept'}
            </button>
            <button
              onClick={() => acceptMutation.mutate('reject')}
              disabled={acceptMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 disabled:opacity-50"
            >
              {pendingAction === 'reject' ? 'Rejecting...' : 'Reject'}
            </button>
          </div>
        </div>
      )}

      {acceptError && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{acceptError}</p>
        </div>
      )}

      {/* Site Context (read-only for vendor) */}
      {site.context && (
        <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Site Context</h3>
              <p className="text-xs text-slate-500 mt-0.5">Set by the client. Tells the scoring engine what kind of site this is.</p>
            </div>
            {/* Vendor can suggest at any time as long as they're the
                site's vendor — status (pending/active) is irrelevant.
                Backend enforces the same rule. */}
            {site.context.completed && (
              <button
                onClick={() => setSuggestModalOpen(true)}
                className="text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                Suggest a change
              </button>
            )}
          </div>

          {!site.context.completed ? (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
              Site context is incomplete. The client will add details soon.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                <div>
                  <p className="text-xs text-slate-500">Site Type</p>
                  <p className="text-slate-900 capitalize">{site.context.site_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Coverage</p>
                  <p className="text-slate-900 capitalize">{site.context.coverage_type}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Hours</p>
                  <p className="text-slate-900">{site.context.coverage_hours}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Intensity</p>
                  <p className="text-slate-900 capitalize">{site.context.patrol_intensity}</p>
                </div>
              </div>
              {(site.context.primary_objective.length > 0 || site.context.top_risks.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm pt-3 border-t border-slate-100">
                  {site.context.primary_objective.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Primary Objectives</p>
                      <p className="text-slate-700 capitalize">{site.context.primary_objective.map((o) => o.replace(/_/g, ' ')).join(', ')}</p>
                    </div>
                  )}
                  {site.context.top_risks.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Top Risks</p>
                      <p className="text-slate-700 capitalize">{site.context.top_risks.map((r) => r.replace(/_/g, ' ')).join(', ')}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Suggest context change modal */}
      <SuggestContextChangeModal
        siteId={id!}
        siteName={site.name}
        isOpen={suggestModalOpen}
        onClose={() => setSuggestModalOpen(false)}
      />

      {/* Info + Score row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Site Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Client</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.client_org_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
              <dd className="mt-1">
                <StatusBadge label={site.vendor_assignment_status} variant={statusVariant(site.vendor_assignment_status)} />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Address</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.address}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">City / State</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.city}, {site.state} {site.zip_code}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Timezone</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.timezone}</dd>
            </div>
            {site.ingestion_email && (
              <div>
                <dt className="text-xs font-medium text-slate-400 uppercase">Ingestion Email</dt>
                <dd className="mt-1 flex items-center gap-2">
                  <span className="text-sm text-slate-900 break-all">{site.ingestion_email}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(site.ingestion_email!); }}
                    className="flex-shrink-0 p-1 text-slate-400 hover:text-blue-600 transition-colors"
                    title="Copy to clipboard"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                    </svg>
                  </button>
                </dd>
              </div>
            )}
          </dl>
        </div>
        <ScoreCard label="Site Score" score={site.score} previousScore={previousScore} size="lg" />
      </div>

      {/* Feature 2: Trust labels */}
      {site.trust_stack && (
        <div className="mb-8">
          <TrustStack data={site.trust_stack} variant="full" />
        </div>
      )}

      {/* Patrol plan the client has defined (read + acknowledge). Only
          shown once we've been assigned; a rejected/pending vendor
          still can't hit the endpoint per the viewset filter. */}
      {site.vendor_assignment_status === 'active' && (
        <VendorPatrolPlanSection siteId={id!} />
      )}

      {/* Score Trend Chart */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Score Trend</h2>
        <ScoreTrendChart snapshots={snapshots} />
      </div>

      {/* Rule Triggers */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <RuleTriggerList
          triggers={ruleTriggers?.results ?? []}
          title="Recent Rule Triggers"
        />
      </div>

      {/* Assigned Guards */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Assigned Guards ({site.assigned_guards?.length ?? 0})
        </h2>
        {site.assigned_guards && site.assigned_guards.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Assigned Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {site.assigned_guards.map((guard) => (
                  <tr
                    key={guard.id}
                    className="cursor-pointer hover:bg-blue-50/50"
                    onClick={() => navigate(`/vendor/guards/${guard.id}`)}
                  >
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{guard.name}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`font-semibold ${getScoreColorClass(getScoreColor(guard.score))}`}>
                        {toGGScore(guard.score)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {new Date(guard.assigned_date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No guards assigned to this site yet.</p>
        )}
      </div>
    </>
  );
}
